// src/components/layout/Navbar.tsx
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/hermes_terminal_cubism.png";
import { useAuth } from "@/components/auth/AuthContext";
import { useAssistantPanel } from "@/hooks/workspace";
import { ProfileDropdown, SearchComponent } from "@/components/common";
import { DynamicIcon } from "lucide-react/dynamic";
import type { SearchSector } from "@/types/search";

interface NavbarProps {
  onMobileMenuToggle?: (() => void) | undefined;
  showMobileMenuButton?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  onMobileMenuToggle,
  showMobileMenuButton = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const { isOpen: assistantPanelOpen } = useAssistantPanel();
  const [showSearch, setShowSearch] = useState(false);

  const handleNavigateToTicker = (ticker: string) => {
    navigate(`/app/tokens/${ticker.toLowerCase()}`);
  };

  const handleNavigateToSector = (sector: SearchSector) => {
    navigate(sector.route);
  };

  return (
    <>
      <nav className="navbar bg-gray-900 shadow-sm fixed top-0 left-0 right-0 z-50 h-14 border-b border-white/10">
        <div className="w-full px-4">
          <div className="flex items-center justify-between h-14 w-full gap-3">
            {/* Left: Mobile menu button and Logo */}
            <div className="flex items-center gap-3">
              {showMobileMenuButton && (
                <button
                  onClick={onMobileMenuToggle}
                  className="md:hidden p-2 rounded-md hover:bg-gray-700 transition-colors"
                  aria-label="Toggle mobile menu"
                >
                  <DynamicIcon name="menu" className="w-5 h-5 text-white" />
                </button>
              )}
              
              <Link
                to="/app"
                className="flex items-center space-x-3 p-2 rounded-lg transition-all duration-300 hover:bg-base-100"
              >
                <img
                  src={logo}
                  alt="Hermes Terminal Logo"
                  className="h-7 w-auto object-contain md:h-8"
                />
                <span className="text-lg font-normal text-white font-sans hidden sm:block">HERMES</span>
              </Link>
            </div>

            {/* Center: Search (desktop) */}
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <SearchComponent
                placeholder="Search"
                onSelectToken={(ticker) => handleNavigateToTicker(ticker)}
                onSelectSector={(sector) => handleNavigateToSector(sector)}
              />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Mobile search toggle */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="md:hidden p-2 rounded-md hover:bg-gray-700 transition-colors"
                aria-label="Toggle search"
              >
                <DynamicIcon name="search" className="w-5 h-5 text-white" />
              </button>

              {/* Desktop actions */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (location.pathname.startsWith("/app/assistant")) {
                      // Already on full page; do nothing
                      return;
                    }
                    const evt = new CustomEvent("abi:assistant:toggle");
                    window.dispatchEvent(evt);
                  }}
                  className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    assistantPanelOpen
                      ? "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  <DynamicIcon name="sparkles" className={`w-4 h-4 ${assistantPanelOpen ? "text-violet-400" : ""}`} />
                  <span className="hidden sm:inline">Ask AI</span>
                  <span className="sm:hidden">AI</span>
                  <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 bg-gray-800 rounded border border-gray-700">
                    <span className="text-[9px]">⌘</span>K
                  </kbd>
                </button>
                {isAuthenticated ? (
                  <ProfileDropdown />
                ) : (
                  <button
                    type="button"
                    onClick={() => void loginWithRedirect()}
                    className="px-3 sm:px-4 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
                  >
                    Login
                  </button>
                )}
              </div>

              {/* Mobile login/profile button */}
              {!isAuthenticated && (
                <button
                  type="button"
                  onClick={() => void loginWithRedirect()}
                  className="md:hidden px-3 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition-colors"
                >
                  Login
                </button>
              )}
            </div>
          </div>

          {/* Mobile search bar */}
          {showSearch && (
            <div className="md:hidden py-3 border-t border-white/10 px-3">
              <div className="relative">
                <SearchComponent
                  placeholder="Search"
                  autoFocus
                  onSelectToken={(ticker) => {
                    handleNavigateToTicker(ticker);
                    setShowSearch(false);
                  }}
                  onSelectSector={(sector) => {
                    handleNavigateToSector(sector);
                    setShowSearch(false);
                  }}
                />
                <button
                  onClick={() => setShowSearch(false)}
                  className="absolute right-2 top-2 p-1 hover:bg-gray-700 rounded"
                  aria-label="Close search"
                >
                  <DynamicIcon name="x" className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer to prevent content from going under fixed navbar */}
      <div className={`h-14 ${showSearch ? 'md:h-14' : ''}`} />
    </>
  );
};

export default Navbar;
